import { defineMessages } from 'react-intl'

/**
 * Localised labels for svc-search standardised amenity categories.
 * API matching values are derived from defaultMessage so filter boosting stays stable.
 */
export const standardisedAmenityMessages = defineMessages({
  airConditioning: {
    id: 'fu21wunLE8fCLS3zxFhp',
    defaultMessage: 'Air conditioning',
  },
  balcony: {
    id: '9S9ocq2Ck7yWD46NJYwL',
    defaultMessage: 'Balcony',
  },
  bathtub: {
    id: 'cjCTXY1X72wtLhMttwa4',
    defaultMessage: 'Bathtub',
  },
  cityViews: {
    id: 'KjZqg0JDvUUiXFrJMPh-',
    defaultMessage: 'City views',
  },
  coffeeMachine: {
    id: '0JMc3kyRyOhx0xJSrl2a',
    defaultMessage: 'Coffee machine',
  },
  desk: {
    id: 'rw1YVWaffsv1DMgw4hET',
    defaultMessage: 'Desk',
  },
  gardenViews: {
    id: '9V-PNUs6lzzRwvrsODHO',
    defaultMessage: 'Garden views',
  },
  kitchen: {
    id: 'ALj01RyEisF-jpkXN1ce',
    defaultMessage: 'Kitchen',
  },
  kitchenette: {
    id: '-3cweqQcFQxZk2GRA3wb',
    defaultMessage: 'Kitchenette',
  },
  laundryFacilities: {
    id: 'mWEuEIjOFM-D60C6xDvZ',
    defaultMessage: 'Laundry facilities',
  },
  mountainViews: {
    id: 'S8kPkPq0-XzJrclMvGAo',
    defaultMessage: 'Mountain views',
  },
  oceanViews: {
    id: 'uPpVhOiGcFd1zzVYu4US',
    defaultMessage: 'Ocean views',
  },
  outdoorSpace: {
    id: '-tC6Rkr_Qcyx_RdF6dHR',
    defaultMessage: 'Outdoor space',
  },
  poolViews: {
    id: 'ma2vagbWbbnR8m73O8nh',
    defaultMessage: 'Pool views',
  },
  privateParking: {
    id: 'M6KsYrIXsZk3Ra_sO2Ds',
    defaultMessage: 'Private parking',
  },
  privatePool: {
    id: 'FfduTuQadH3ZnKWb2Ap7',
    defaultMessage: 'Private pool',
  },
  rainShower: {
    id: '_d4uciWMrKbyQV57zriC',
    defaultMessage: 'Rain shower',
  },
  spaBath: {
    id: 'OkkblYmjooIPNgFq5i-F',
    defaultMessage: 'Spa bath',
  },
  terrace: {
    id: 'Hv21PwaDNPGnvoDB_ojV',
    defaultMessage: 'Terrace',
  },
  waterViews: {
    id: 'No86uvl5m43DR4RCH_3p',
    defaultMessage: 'Water views',
  },
})

/**
 * Stable svc-search standardised amenity values used for API and redux filter matching.
 * Values are sourced from message defaultMessage so they stay aligned with filter analytics.
 */
export const STANDARDISED_AMENITY = {
  AIR_CONDITIONING: standardisedAmenityMessages.airConditioning.defaultMessage,
  BALCONY: standardisedAmenityMessages.balcony.defaultMessage,
  BATHTUB: standardisedAmenityMessages.bathtub.defaultMessage,
  CITY_VIEWS: standardisedAmenityMessages.cityViews.defaultMessage,
  COFFEE_MACHINE: standardisedAmenityMessages.coffeeMachine.defaultMessage,
  DESK: standardisedAmenityMessages.desk.defaultMessage,
  GARDEN_VIEWS: standardisedAmenityMessages.gardenViews.defaultMessage,
  KITCHEN: standardisedAmenityMessages.kitchen.defaultMessage,
  KITCHENETTE: standardisedAmenityMessages.kitchenette.defaultMessage,
  LAUNDRY_FACILITIES: standardisedAmenityMessages.laundryFacilities.defaultMessage,
  MOUNTAIN_VIEWS: standardisedAmenityMessages.mountainViews.defaultMessage,
  OCEAN_VIEWS: standardisedAmenityMessages.oceanViews.defaultMessage,
  OUTDOOR_SPACE: standardisedAmenityMessages.outdoorSpace.defaultMessage,
  POOL_VIEWS: standardisedAmenityMessages.poolViews.defaultMessage,
  PRIVATE_PARKING: standardisedAmenityMessages.privateParking.defaultMessage,
  PRIVATE_POOL: standardisedAmenityMessages.privatePool.defaultMessage,
  RAIN_SHOWER: standardisedAmenityMessages.rainShower.defaultMessage,
  SPA_BATH: standardisedAmenityMessages.spaBath.defaultMessage,
  TERRACE: standardisedAmenityMessages.terrace.defaultMessage,
  WATER_VIEWS: standardisedAmenityMessages.waterViews.defaultMessage,
} as const

export type StandardisedAmenity = typeof STANDARDISED_AMENITY[keyof typeof STANDARDISED_AMENITY]

/**
 * Map for boosting amenities/popular facilities. We're essentially copying the map from svc-search
 * https://github.com/lux-group/svc-search/blob/master/src/lib/amenities.ts
 *
 * How this works in svc-search:
 * Source Data                    →  AmenityMapping  →  OpenSearch Index (this is what CP gets)
 * ─────────────────────────────────────────────────────────────────────
 *
 * Examples:
 * - "Nespresso coffee machine"     →  "Coffee machine"  →  room_rate_config.amenities
 * - "Heated plunge pool"           →  "Private pool"    →  room_rate_config.amenities
 * - "Outdoor pool"                 →  "Private pool"    →  room_rate_config.amenities
 * - "Walk-in shower"               →  "Rain shower"     →  room_rate_config.amenities
 *
 * In Customer Portal:
 * - The map values (right side) represent standardized amenity categories (e.g., "Private pool", "Rain shower", "Coffee machine")
 * - These 20 unique category values are used as a proxy for matching against active amenity filters
 * - When a user has an active amenity filter, matching amenities are boosted to the top of search tiles
 * - Filter state is tracked via redux historicalAction (see useFilterChangeTracking in HotelSearchPageFilterPane)
 *
 * Keys are raw svc-search amenity names used for lookup only and are not localised.
 */
export const STANDARDISED_AMENITIES_MAP: Record<string, StandardisedAmenity> = {
  'Air conditioning': STANDARDISED_AMENITY.AIR_CONDITIONING,
  'Air Conditioning': STANDARDISED_AMENITY.AIR_CONDITIONING,
  'Balcony': STANDARDISED_AMENITY.BALCONY,
  'Furnished balcony': STANDARDISED_AMENITY.BALCONY,
  'Private balcony': STANDARDISED_AMENITY.BALCONY,
  'Private veranda': STANDARDISED_AMENITY.BALCONY,
  'Bath': STANDARDISED_AMENITY.BATHTUB,
  'Bath tub': STANDARDISED_AMENITY.BATHTUB,
  'Bathtub': STANDARDISED_AMENITY.BATHTUB,
  'Bathtub and shower': STANDARDISED_AMENITY.BATHTUB,
  'Outdoor bathtub': STANDARDISED_AMENITY.BATHTUB,
  'Separate bathtub': STANDARDISED_AMENITY.BATHTUB,
  'Separate bathtub and shower': STANDARDISED_AMENITY.BATHTUB,
  'Separate shower and bathtub': STANDARDISED_AMENITY.BATHTUB,
  'Shower and bathtub': STANDARDISED_AMENITY.BATHTUB,
  'Sunken tub': STANDARDISED_AMENITY.BATHTUB,
  'City views': STANDARDISED_AMENITY.CITY_VIEWS,
  'Bella coffee machine': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Coffee machine': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Coffee maker': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Coffesso coffee machine': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Espresso coffee machine': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Keurig coffee maker': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Nespresso coffee machine': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Nespresso coffee machine and a selection of T2 teas and hot chocolate': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Nespresso coffee machine with complimentary Nespresso coffee and tea': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Desk': STANDARDISED_AMENITY.DESK,
  'Desk with myriad charging ports': STANDARDISED_AMENITY.DESK,
  'Desk/office space': STANDARDISED_AMENITY.DESK,
  'Executive desk': STANDARDISED_AMENITY.DESK,
  'Full-size executive writing desk': STANDARDISED_AMENITY.DESK,
  'Large working desk': STANDARDISED_AMENITY.DESK,
  'Luxury business desk': STANDARDISED_AMENITY.DESK,
  'Spacious work desk': STANDARDISED_AMENITY.DESK,
  'Work desk': STANDARDISED_AMENITY.DESK,
  'Work desk and chair': STANDARDISED_AMENITY.DESK,
  'Work desk with data ports': STANDARDISED_AMENITY.DESK,
  'Work desk with multi-national electric and data ports': STANDARDISED_AMENITY.DESK,
  'Workdesk': STANDARDISED_AMENITY.DESK,
  'Workspace': STANDARDISED_AMENITY.DESK,
  'Garden views': STANDARDISED_AMENITY.GARDEN_VIEWS,
  'Full kitchen': STANDARDISED_AMENITY.KITCHEN,
  'Fully equipped kitchen': STANDARDISED_AMENITY.KITCHEN,
  'Fully-equipped kitchen': STANDARDISED_AMENITY.KITCHEN,
  'Fully equipped kitchenette': STANDARDISED_AMENITY.KITCHENETTE,
  'Laundry facilities': STANDARDISED_AMENITY.LAUNDRY_FACILITIES,
  'Mountain views': STANDARDISED_AMENITY.MOUNTAIN_VIEWS,
  'Mountain Views': STANDARDISED_AMENITY.MOUNTAIN_VIEWS,
  'Beach views': STANDARDISED_AMENITY.OCEAN_VIEWS,
  'Beachfront': STANDARDISED_AMENITY.OCEAN_VIEWS,
  'Ocean views': STANDARDISED_AMENITY.OCEAN_VIEWS,
  'Partial ocean views': STANDARDISED_AMENITY.OCEAN_VIEWS,
  'Outdoor Furniture': STANDARDISED_AMENITY.OUTDOOR_SPACE,
  'Terrace': STANDARDISED_AMENITY.OUTDOOR_SPACE,
  'Garden': STANDARDISED_AMENITY.OUTDOOR_SPACE,
  'Patio': STANDARDISED_AMENITY.OUTDOOR_SPACE,
  'Courtyard': STANDARDISED_AMENITY.OUTDOOR_SPACE,
  'Pool views': STANDARDISED_AMENITY.POOL_VIEWS,
  'Free onsite private parking': STANDARDISED_AMENITY.PRIVATE_PARKING,
  'Free private parking': STANDARDISED_AMENITY.PRIVATE_PARKING,
  'Private Parking': STANDARDISED_AMENITY.PRIVATE_PARKING,
  'Private parking': STANDARDISED_AMENITY.PRIVATE_PARKING,
  'Heated plunge pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Infinity pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Plunge pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Plunge Pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private freshwater pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private infinity pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private jetpool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private outdoor Jacuzzi pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private outdoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private outdoor pool and terrace': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private plunge pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private pool, pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Private swimming pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Spa pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Indoor rain shower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Outdoor rain shower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Outdoor shower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Rain shower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Rainfall showerhead': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Rainshower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Walk-in shower': STANDARDISED_AMENITY.RAIN_SHOWER,
  'Hot tub': STANDARDISED_AMENITY.SPA_BATH,
  'Jacuzzi': STANDARDISED_AMENITY.SPA_BATH,
  'Jet tub': STANDARDISED_AMENITY.SPA_BATH,
  'Spa bath': STANDARDISED_AMENITY.SPA_BATH,
  'Private outdoor deck': STANDARDISED_AMENITY.TERRACE,
  'Private patio': STANDARDISED_AMENITY.TERRACE,
  'Bay views': STANDARDISED_AMENITY.WATER_VIEWS,
  'Canal views': STANDARDISED_AMENITY.WATER_VIEWS,
  'Lagoon views': STANDARDISED_AMENITY.WATER_VIEWS,
  'Lake views': STANDARDISED_AMENITY.WATER_VIEWS,
  'River or ocean views': STANDARDISED_AMENITY.WATER_VIEWS,
  'Water views': STANDARDISED_AMENITY.WATER_VIEWS,
  '24-hour pool access': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Access to nearby indoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Access to nearby outdoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  "Children's pool": STANDARDISED_AMENITY.PRIVATE_POOL,
  'Coffee/tea maker': STANDARDISED_AMENITY.COFFEE_MACHINE,
  'Exercise/lap pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Heated pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Indoor/outdoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Indoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Indoor pool (surcharge)': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Indoor private mineral hot springs (Japanese-style onsen)': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Kitchen': STANDARDISED_AMENITY.KITCHEN,
  'Kitchenette': STANDARDISED_AMENITY.KITCHENETTE,
  'Lazy river': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Natural pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Outdoor pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Outdoor pool (surcharge)': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Outdoor seasonal pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Rooftop pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Shared/communal pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Spa tub': STANDARDISED_AMENITY.SPA_BATH,
  'Spa tub (surcharge)': STANDARDISED_AMENITY.SPA_BATH,
  'Swimming pool': STANDARDISED_AMENITY.PRIVATE_POOL,
  'Wheelchair-accessible pool': STANDARDISED_AMENITY.PRIVATE_POOL,
}
